import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { cloneDeep, isEmpty, omit } from 'lodash';
import { ValidationService } from 'shared/services';
import { ROOM_STATUS, EXAM_MODES, ACT_NATIONAL_TIMING_NAMES } from 'shared/constants';
import {
  Input,
  CheckboxList,
  Select,
  MultiRadio,
  Modal as WarningModal,
  AlertNotice,
} from 'shared/components';
import { isRoomOnline, mapTypeOptions, mapTimingOptions } from 'shared/helpers';
import './RoomInformation.scss';

const validationRules = {
  name: ['required'],
  capacity: ['required'],
  timing: ['required'],
  location: ['required'],
};
const errorStrings = {
  name: {
    required: 'errors.roomNameRequired',
  },
  capacity: {
    required: 'errors.roomCapacityRequired',
    gt: 'errors.roomCapacityMinimum',
  },
  timing: {
    required: 'errors.timingCategoryRequired',
  },
  location: {
    required: 'errors.locationsRequired',
  },
};

const toChecklistData = (data, existing, key) =>
  data.map((item) => ({
    key: `${key}-${item.id}`,
    label: item.name,
    checked: existing && existing.findIndex((elem) => item.id === elem.facilityId) > -1,
  }));

const findLocation = (locations, locationId) => locations.find((loc) => loc.id === locationId);

const checkCapacities = (locations, locationId) =>
  locations.some((loc) => {
    let flagOnline;
    let flagPaper;
    if (loc.id === locationId) {
      loc.locationEvent.locationEventCapacities.forEach((locExam) => {
        if (locExam.capacity && locExam.capacityType.onlineFlag) {
          flagOnline = true;
        } else if (locExam.capacity && !locExam.capacityType.onlineFlag) {
          flagPaper = true;
        }
      });
    }
    return flagOnline && flagPaper;
  });

const checkPaperCapacity = (locations, locationId) =>
  locations.some(
    (loc) =>
      loc.id === locationId &&
      loc.locationEvent.locationEventCapacities.some(
        (locExam) => !locExam.capacityType.onlineFlag && locExam.capacity
      )
  );

const checkOnlineCapacity = (locations, locationId) =>
  locations.some(
    (loc) =>
      loc.id === locationId &&
      loc.locationEvent.locationEventCapacities.some(
        (locExam) => locExam.capacityType.onlineFlag && locExam.capacity
      )
  );

const trimRightSpaces = (str) => str.replace(/\s*$/, '');

const applyActNatlTimingNames = (options, programCode) => {
  if (programCode !== 'actnatl') return options;
  return options.map((opt) => ({
    ...opt,
    label: ACT_NATIONAL_TIMING_NAMES[opt.key] || opt.label,
  }));
};

class RoomInformation extends Component {
  constructor(props) {
    super(props);
    const { room, roomOptions } = props;

    const selectedRoomType = room.roomType && room.roomType.id;
    const selectedLocationId =
      room && !isEmpty(room.locationEvent)
        ? room.locationEvent.locationId
        : props.locations.length === 1
        ? props.locations[0].id
        : '';
    const chooseLater =
      room.id || room.name
        ? checkPaperCapacity(props.locations, selectedLocationId)
          ? -1
          : null
        : room.id;
    const selectedExamMode =
      room && room.roomType
        ? isRoomOnline(room.roomType)
          ? EXAM_MODES.online
          : EXAM_MODES.paper
        : chooseLater === -1
        ? EXAM_MODES.paper
        : '';
    const roomTypesOnlineFlag =
      room && room.roomType
        ? isRoomOnline(room.roomType)
        : selectedExamMode
        ? selectedExamMode === EXAM_MODES.online
        : checkOnlineCapacity(props.locations, selectedLocationId);

    this.state = {
      room: { ...room },
      timingOptions: applyActNatlTimingNames(
        mapTimingOptions(props.roomTypes, props.mappedRoomTiming, room.roomType?.id || null),
        props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code
      ),
      examModeOptions: Object.keys(EXAM_MODES).map((key) => ({
        key,
        label: EXAM_MODES[key],
        value: EXAM_MODES[key],
      })),
      selectedRoomType: selectedRoomType || chooseLater,
      roomTypeOptions: mapTypeOptions(
        props.roomTypes,
        roomTypesOnlineFlag,
        false,
        true,
        this.props?.tcEvent?.studyFlag
      ),
      facilityOptions: room
        ? toChecklistData(roomOptions.facilities, room.roomFacilities, 'facility')
        : [],
      selectedLocation: selectedLocationId,
      selectedExamMode: selectedExamMode,
      errors: {},
      warningModalOpen: false,
      mismatchModalOpen: false,
      roomModeSwitchModalOpen: false,
      bothCapacitiesFlag: checkCapacities(props.locations, selectedLocationId),
      timingWarningModalOpen: false,
    };

    this.onChange = this.onChange.bind(this);
    this.onCapacityChange = this.onCapacityChange.bind(this);
    this.onExamModeChange = this.onExamModeChange.bind(this);
    this.onTimingChange = this.onTimingChange.bind(this);
    this.onFacilityChange = this.onFacilityChange.bind(this);
    this.onRoomTypeChange = this.onRoomTypeChange.bind(this);
    this.handleLocationChange = this.handleLocationChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onContinue = this.onContinue.bind(this);
    this.toggleWarningModal = this.toggleWarningModal.bind(this);
    this.showWarningModal = this.showWarningModal.bind(this);
    this.checkRoomName = this.checkRoomName.bind(this);
    this.checkRoomNameExistence = this.checkRoomNameExistence.bind(this);
  }

  componentDidMount() {
    this.props.resetRoomMaterials();
  }

  componentWillUnmount() {
    this.props.resetRoomMaterials();
  }

  onCapacityChange(e) {
    let { value } = e.target;
    let errors = {};

    if (+value < 0) {
      value = 0;
    }
    if (+value > 999) {
      return;
    }

    if (+value > 200) {
      errors.capacity = ['errors.roomMaximumCapacity'];
    } else {
      errors.capacity = '';
    }

    const nextState = {
      ...this.state,
      room: { ...this.state.room, [e.target.name]: value ? +value : undefined },
    };

    this.setState((prevState) => ({
      ...nextState,
      errors: {
        ...prevState.errors,
        ...errors,
      },
    }));
  }

  onChange(e) {
    const { value, name } = e.target;

    if (!ValidationService.roomName(value)) {
      return;
    }

    this.setState((prevState) => ({
      room: { ...this.state.room, [name]: value },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
    }));
  }

  checkRoomNameExistence(locationId, roomName) {
    const location = this.props.locations.find((loc) => loc.id === locationId);
    this.props.checkRoomNameExistence(location.locationEvent.id, roomName).then(() => {
      if (this.props.roomNameExistent) {
        this.setState((prevState) => ({
          errors: {
            ...prevState.errors,
            name: ['roomBuilder.roomNameError'],
          },
        }));
      }
    });
  }

  checkRoomName(e) {
    const { selectedLocation } = this.state;
    const { value } = e.target;
    if (selectedLocation && !isEmpty(value) && trimRightSpaces(value) !== this.props.room.name) {
      this.checkRoomNameExistence(selectedLocation, e.target.value);
    }
  }

  onExamModeChange(e) {
    const { roomTypes } = this.props;
    const { room } = this.state;
    const { value } = e.target;

    if (room.id) {
      this.props.getRoomMaterials(room.id);
    } else {
      this.props.resetRoomMaterials();
    }

    this.setState((prevState) => ({
      selectedExamMode: value,
      roomTypeOptions: mapTypeOptions(
        roomTypes,
        value === EXAM_MODES.online,
        false,
        true,
        this.props?.tcEvent?.studyFlag
      ),
      selectedRoomType: '',
      room: {
        ...prevState.room,
        timing: '',
      },
      errors: {
        ...this.state.errors,
        examMode: '',
      },
    }));
  }

  onTimingChange(e) {
    const { value } = e.target;

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        timing: value,
      },
      errors: {
        ...prevState.errors,
        timing: '',
      },
    }));
  }

  onRoomTypeChange(e) {
    const { value } = e.target;

    const programCode = this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code;
    const timingOptions = applyActNatlTimingNames(
      mapTimingOptions(this.props.roomTypes, this.props.mappedRoomTiming, value),
      programCode
    );

    this.setState((prevState) => ({
      selectedRoomType: +value,
      timingOptions,
      room: {
        ...prevState.room,
        timing: '',
      },
      errors: {
        ...prevState.errors,
        roomType: '',
        timing: '',
      },
    }));
  }

  onFacilityChange(e) {
    const roomFacilities = [...this.state.room.roomFacilities];
    const targetId = +e.target.name.split('-')[1];
    const selectedFacility = this.props.roomOptions.facilities.find((fac) => +fac.id === +targetId);
    const facility = this.state.room.id
      ? { facilityId: selectedFacility.id, roomId: this.state.room.id }
      : { facilityId: selectedFacility.id };

    if (e.target.checked) {
      roomFacilities.push(facility);
    } else {
      roomFacilities.splice(
        roomFacilities.findIndex((f) => f.facilityId === facility.facilityId),
        1
      );
    }

    this.setState({
      room: { ...this.state.room, roomFacilities },
      facilityOptions: toChecklistData(
        this.props.roomOptions.facilities,
        roomFacilities,
        'facility'
      ),
    });
  }

  handleLocationChange(e) {
    const { value } = e.target;
    const capacityFlag = checkCapacities(this.props.locations, +value);
    const { room, selectedLocation } = this.state;

    this.setState((prevState) => ({
      selectedLocation: +value,
      errors: {
        ...prevState.errors,
        selectedLocation: '',
      },
      bothCapacitiesFlag: capacityFlag,
      selectedExamMode: '',
      selectedRoomType: '',
      room: {
        ...prevState.room,
        timing: '',
      },
      roomTypeOptions: mapTypeOptions(
        this.props.roomTypes,
        checkOnlineCapacity(this.props.locations, +value),
        false,
        true,
        this.props?.tcEvent?.studyFlag
      ),
    }));

    if (
      room.name &&
      value !== selectedLocation &&
      trimRightSpaces(room.name) !== this.props.room.name
    ) {
      this.checkRoomNameExistence(+value, room.name);
    }
  }

  async onContinue() {
    const { roomTypes } = this.props;
    const savedRoom = this.props.room;
    const room = cloneDeep(this.state.room);
    const location = this.props.locations.find((loc) => loc.id === this.state.selectedLocation);
    room.locationEvent = {
      id: location.locationEvent.id,
      locationId: location.id,
    };
    room.roomType = roomTypes.find((roomType) => roomType.id === +this.state.selectedRoomType);
    if (savedRoom.capacity && savedRoom.capacity !== room.capacity) {
      if (!(await this.showWarningModal('warningModalOpen'))) {
        return;
      }
    }

    if (
      savedRoom.id &&
      savedRoom.timing !== room.timing &&
      !isEmpty(savedRoom.timing || room.timing)
    ) {
      if (!(await this.showWarningModal('timingWarningModalOpen'))) {
        return;
      }
    }

    if (this.props.countMaterials) {
      if (!(await this.showWarningModal('roomModeSwitchModalOpen'))) {
        return;
      }
    }

    const mismatch = await this.props.checkRoomMismatch(room);
    if (mismatch) {
      if (!(await this.showWarningModal('mismatchModalOpen'))) {
        return;
      }
    }

    this.props.onContinue(room);
  }

  showWarningModal(modal) {
    this.setState({ [modal]: true });
    return new Promise((res) => {
      this.resolve = res;
    });
  }

  toggleWarningModal(confirm) {
    this.setState({
      warningModalOpen: false,
      mismatchModalOpen: false,
      roomModeSwitchModalOpen: false,
      timingWarningModalOpen: false,
    });
    this.resolve(confirm);
  }

  async onSubmit(e) {
    e.preventDefault();
    const { room, selectedRoomType, selectedExamMode, bothCapacitiesFlag, selectedLocation } =
      this.state;
    const savedRoom = this.props.room;

    let errors = ValidationService.validate(room, validationRules, errorStrings);

    if (this.state.errors.name) {
      errors.name = this.state.errors.name;
    } else if (
      selectedLocation &&
      !isEmpty(room.name) &&
      trimRightSpaces(room.name) !== savedRoom.name
    ) {
      const location = this.props.locations.find((loc) => loc.id === selectedLocation);
      await this.props.checkRoomNameExistence(location.locationEvent.id, room.name);
      if (this.props.roomNameExistent) {
        errors.name = ['roomBuilder.roomNameError'];
      }
    }
    if (room.capacity > 200) {
      errors.capacity = ['errors.roomMaximumCapacity'];
    }

    if (!this.state.selectedLocation) {
      errors.selectedLocation = ['errors.locationsRequired'];
    }

    if (!selectedExamMode && bothCapacitiesFlag) {
      errors = {
        ...omit(errors, ['timing']),
        examMode: ['errors.roomModeRequired'],
      };
    } else if (!selectedRoomType) {
      errors.roomType = ['errors.selectType'];
    }

    if (!this.state.room.timing && selectedRoomType) {
      errors.timing = ['errors.timingCategoryRequired'];
    }

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    }

    this.onContinue();
  }

  render() {
    const {
      room,
      errors,
      selectedLocation,
      examModeOptions,
      selectedExamMode,
      bothCapacitiesFlag,
      timingOptions,
      roomTypeOptions,
      selectedRoomType,
    } = this.state;
    const { tc, tcEvent, locations, manualTimerFlag } = this.props;

    const showFacilities = !!tcEvent.testingEvent?.adminId?.cycleId?.showFacilitiesFlag;

    const roomIsInactive = !manualTimerFlag && room.status === ROOM_STATUS.inactive;
    return (
      !isEmpty(room) &&
      !room.loading && (
        <div className="room-information">
          <h4 className="mb-3">{this.props.t('roomBuilder.roomInformationTitle')}</h4>
          <p className="mb-1">{tc.name}</p>
          <p className="mb-1">{tcEvent.testingEvent.name}</p>
          <p className="mb-4">
            <span>{tc.tcType.name}</span>
            {selectedLocation ? (
              <span>, {findLocation(locations, selectedLocation).name}</span>
            ) : null}
          </p>
          <form onSubmit={this.onSubmit} noValidate>
            <Input
              containerClassName="room-name-input"
              type="text"
              name="name"
              value={room.name}
              label="roomBuilder.roomNameLabel"
              onBlur={this.checkRoomName}
              onChange={this.onChange}
              errors={errors.name}
              infoText={this.props.t('roomBuilder.roomNameInfoText')}
              required
            />
            <div className="">
              <Select
                className="select-location"
                label={this.props.t('roomBuilder.roomLocationTitle')}
                options={locations.map((element) => ({
                  value: element.id,
                  label: `${element.name}${
                    element.area && element.area.length > 0 && `, ${element.area}`
                  }`,
                }))}
                value={selectedLocation}
                name="locationId"
                onChange={this.handleLocationChange}
                disabled={
                  manualTimerFlag || room.status !== ROOM_STATUS.inactive || locations.length < 2
                }
                infoText={this.props.t('roomBuilder.locationInfoText')}
                errors={errors.selectedLocation}
                emptyDefault
              />
            </div>
            <div className="">
              <label
                className={`${errors.capacity ? 'label-error' : ''} fw-bold w-100`}
                htmlFor="capacity">
                {this.props.t('roomBuilder.roomMaxCapacityLabel')}
              </label>
              <Input
                containerClassName="capacity-input"
                type="number"
                name="capacity"
                value={room.capacity}
                onChange={this.onCapacityChange}
                infoText={this.props.t('roomBuilder.maxCapacityMessage')}
                errors={errors.capacity}
                required
              />
            </div>
            {selectedLocation ? (
              roomIsInactive && (
                <div>
                  {bothCapacitiesFlag && (
                    <div className="">
                      <MultiRadio
                        radioList={examModeOptions}
                        checkedValue={selectedExamMode}
                        onChange={this.onExamModeChange}
                        title={this.props.t('roomBuilder.roomModeLabel')}
                        errors={errors.examMode}
                        infoText={this.props.t('roomBuilder.roomModeInfoText')}
                        required
                      />
                    </div>
                  )}
                  {(selectedExamMode || !bothCapacitiesFlag) && (
                    <React.Fragment>
                      <div className="">
                        <MultiRadio
                          radioList={roomTypeOptions}
                          checkedValue={selectedRoomType}
                          onChange={this.onRoomTypeChange}
                          title={this.props.t('roomBuilder.roomTypeMessage')}
                          errors={errors.roomType}
                          required
                        />
                      </div>
                      {selectedRoomType && (
                        <div className="">
                          <MultiRadio
                            radioList={timingOptions}
                            checkedValue={room.timing}
                            onChange={this.onTimingChange}
                            title={this.props.t('roomBuilder.timingCategoryMessage')}
                            errors={errors.timing}
                            required
                          />
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>
              )
            ) : (
              <AlertNotice message={this.props.t('roomBuilder.selectModeInfoMessage')} />
            )}

            {showFacilities && (
              <div className="">
                <CheckboxList
                  checkboxes={this.state.facilityOptions}
                  onChange={this.onFacilityChange}
                  title={this.props.t('roomBuilder.facilitiesAvailableLabel')}
                />
              </div>
            )}
            <div className="buttons-container w-100">
              <button type="button" className="btn btn-outline w-50" onClick={this.props.onBack}>
                {this.props.t('common.labels.back')}
              </button>
              <button type="submit" className="btn btn-primary w-50">
                {this.props.t('common.labels.continue')}
              </button>
            </div>
          </form>
          <WarningModal
            closeModal={() => this.toggleWarningModal(false)}
            onConfirm={() => this.toggleWarningModal(true)}
            open={this.state.warningModalOpen}
            modalContent="roomBuilder.capacityModalWarning"
          />
          <WarningModal
            closeModal={() => this.toggleWarningModal(false)}
            onConfirm={() => this.toggleWarningModal(true)}
            hideIcon
            open={this.state.timingWarningModalOpen}
            modalContent="roomBuilder.timingWarningModal"
          />
          <WarningModal
            closeModal={() => this.toggleWarningModal(false)}
            onConfirm={() => this.toggleWarningModal(true)}
            hideIcon
            open={this.state.mismatchModalOpen}
            modalContent="roomBuilder.mismatchModalWarning"
          />
          <WarningModal
            closeModal={() => this.toggleWarningModal(false)}
            onConfirm={() => this.toggleWarningModal(true)}
            hideIcon
            open={this.state.roomModeSwitchModalOpen}
            modalContent="roomBuilder.switchModeToOnlineModal"
          />
        </div>
      )
    );
  }
}

RoomInformation.propTypes = {
  tc: PropTypes.shape({}).isRequired,
  tcEvent: PropTypes.shape({}).isRequired,
  room: PropTypes.shape({
    roomFacilities: PropTypes.arrayOf(PropTypes.shape({})),
    locationEvent: PropTypes.shape({}),
  }).isRequired,
  roomOptions: PropTypes.shape({
    facilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(RoomInformation);
